import React, { useEffect, useRef, useState } from 'react';
import { Box, Fab, FormControlLabel, IconButton, Paper, Switch, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { ATTR } from './attrs';
import { HelpCard } from './HelpCard';
import { useHelp } from './HelpContext';
import { Box as BoxT, HelpTarget } from './types';
import { uiText } from './uiText';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAddon } from '../components/addons/useAddon';

const Z = 1600;
const ui = { [ATTR.ui]: '' };

export interface RectLike { left: number; top: number; right: number; bottom: number; width: number; height: number }
const fromBox = (b: BoxT): RectLike => ({ ...b, width: b.right - b.left, height: b.bottom - b.top });

function targetRect(t: HelpTarget | null, box: BoxT | null): RectLike | null {
    if (box) return fromBox(box);
    if (!t || t.kind === 'missing') return null;
    if (t.kind === 'element') return t.el.isConnected ? t.el.getBoundingClientRect() : null;
    if (t.box) return fromBox(t.box);
    return t.section?.el.isConnected ? t.section.el.getBoundingClientRect() : null;
}

/** Anchor element of a target (what the highlight and the card follow). */
const anchorEl = (t: HelpTarget | null): HTMLElement | null =>
    !t || t.kind === 'missing' ? null : t.kind === 'element' ? t.el : t.section?.el ?? null;

/**
 * While a highlight is showing: re-render on scroll/resize (so it follows the element) and when the DOM
 * changes (so it clears if the element unmounts). Nothing is attached otherwise.
 */
function useTick(enabled: boolean) {
    const [, setN] = useState(0);
    useEffect(() => {
        if (!enabled) return;
        let frame = 0;
        const bump = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => { frame = 0; setN(n => n + 1); });
        };
        window.addEventListener('scroll', bump, true);
        window.addEventListener('resize', bump);
        const mo = typeof MutationObserver === 'function' ? new MutationObserver(bump) : null;
        mo?.observe(document.body, { childList: true, subtree: true });
        return () => {
            window.removeEventListener('scroll', bump, true);
            window.removeEventListener('resize', bump);
            mo?.disconnect();
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [enabled]);
}

/** True while an MUI dialog is open (dialogs portal straight into body, so watching body's children is enough). */
function useDialogOpen(): boolean {
    const has = () => typeof document !== 'undefined' && !!document.querySelector('.MuiDialog-root');
    const [open, setOpen] = useState(has);
    useEffect(() => {
        if (typeof MutationObserver !== 'function') return;
        const mo = new MutationObserver(() => setOpen(has()));
        mo.observe(document.body, { childList: true });
        return () => mo.disconnect();
    }, []);
    return open;
}

const Dim: React.FC<{ rect: RectLike | null }> = ({ rect }) => (
    <Box
        aria-hidden
        sx={rect ? {
            position: 'fixed', zIndex: Z, pointerEvents: 'none', borderRadius: 1,
            left: rect.left - 4, top: rect.top - 4, width: rect.width + 8, height: rect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)', outline: '2px solid', outlineColor: 'primary.main',
        } : { position: 'fixed', inset: 0, zIndex: Z, pointerEvents: 'none', bgcolor: 'rgba(0,0,0,0.5)' }}
    />
);

/** The draggable ? : drop it on a button, field or chip. */
const DropHandle: React.FC = () => {
    const { pickAtPoint } = useHelp();
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const drag = useRef(false);
    return (
        <>
            <Box
                {...ui}
                role="button"
                aria-label="Drag this question mark onto anything to learn about it"
                onPointerDown={e => { drag.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); setPos({ x: e.clientX, y: e.clientY }); }}
                onPointerMove={e => { if (drag.current) setPos({ x: e.clientX, y: e.clientY }); }}
                onPointerUp={e => {
                    if (!drag.current) return;
                    drag.current = false;
                    setPos(null);
                    pickAtPoint(e.clientX, e.clientY);
                }}
                onPointerCancel={() => { drag.current = false; setPos(null); }}
                sx={{
                    width: 36, height: 36, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', touchAction: 'none', flexShrink: 0,
                    userSelect: 'none', fontWeight: 700,
                }}
            >
                ?
            </Box>
            {pos && (
                <Box
                    {...ui}
                    aria-hidden
                    sx={{
                        position: 'fixed', zIndex: Z + 200, left: pos.x - 18, top: pos.y - 18, width: 36, height: 36, borderRadius: '50%',
                        bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', pointerEvents: 'none', fontWeight: 700, boxShadow: 6,
                    }}
                >
                    ?
                </Box>
            )}
        </>
    );
};

const HelpBar: React.FC = () => {
    const { touchMode, setTouchMode, exit } = useHelp();
    const isMobile = useIsMobile();
    return (
        <Paper
            {...ui}
            elevation={6}
            sx={{
                position: 'fixed', zIndex: Z + 100, p: 1,
                // On a phone the bar sits at the TOP so the bottom sheet card and the ? button never collide with it.
                ...(isMobile
                    ? { left: 8, right: 8, top: 8 }
                    : { left: '50%', transform: 'translateX(-50%)', bottom: 16, maxWidth: 'calc(100vw - 96px)' }), display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
            }}
        >
            {!touchMode && <DropHandle />}
            <Typography variant="body2" sx={{ flex: '1 1 160px', minWidth: 0 }}>
                {touchMode ? uiText('barTouch') : uiText('barPointer')}
            </Typography>
            <FormControlLabel
                sx={{ m: 0, flex: '0 1 auto' }}
                control={<Switch size="small" checked={touchMode} onChange={e => setTouchMode(e.target.checked)} inputProps={{ 'aria-label': uiText('touchMode') }} />}
                label={<Typography variant="caption">{uiText('touchMode')}</Typography>}
            />
            <Tooltip title={uiText('leaveEsc')}>
                <IconButton size="small" aria-label={uiText('leave')} onClick={exit}><CloseIcon fontSize="small" /></IconButton>
            </Tooltip>
        </Paper>
    );
};

/**
 * Renders the ? button on every page; everything else mounts only while help mode is on. The ? button is the only
 * way into help mode, so hiding it switches help off when the help add-on is off.
 */
export const HelpLauncher: React.FC = () => {
    const on = useAddon('help');
    return on ? <HelpLauncherOn /> : null;
};

const HelpLauncherOn: React.FC = () => {
    const { active, toggle, exit, target, dragBox, clearTarget } = useHelp();
    const location = useLocation();
    const rect = targetRect(target, dragBox);
    useTick(active && !!target);
    const fabRef = useRef<HTMLButtonElement>(null);
    const isMobile = useIsMobile();
    const dialogOpen = useDialogOpen();

    // The highlighted element was removed from the page: drop the stale highlight and card.
    const gone = (() => { const el = anchorEl(target); return !!el && !el.isConnected; })();
    useEffect(() => { if (gone) clearTarget(); }, [gone, clearTarget]);

    // Leave help mode when the user navigates (the page the card was about is gone).
    const first = useRef(true);
    useEffect(() => {
        if (first.current) { first.current = false; return; }
        exit();
    }, [location.pathname, exit]);

    // Keyboard users land back on the ? button when help mode ends.
    const wasActive = useRef(false);
    useEffect(() => {
        if (wasActive.current && !active) fabRef.current?.focus();
        wasActive.current = active;
    }, [active]);

    return (
        <>
            {active && (
                <>
                    <Dim rect={rect} />
                    <HelpBar />
                    <HelpCard anchor={rect} />
                </>
            )}
            <Tooltip title={active ? uiText('leave') : uiText('openTooltip')}>
                <Fab
                    {...ui}
                    ref={fabRef}
                    size="small"
                    color={active ? 'secondary' : 'primary'}
                    aria-label={active ? uiText('leave') : uiText('open')}
                    aria-pressed={active}
                    onClick={toggle}
                    // Always above dialogs, so help can be opened while a dialog is showing. On a phone a dialog is
                    // full screen with its Save at the bottom right, so the button moves to the bottom left then.
                    sx={{ position: 'fixed', bottom: 16, zIndex: Z + 100, ...(isMobile && dialogOpen ? { left: 16 } : { right: 16 }) }}
                >
                    <HelpOutlineIcon />
                </Fab>
            </Tooltip>
        </>
    );
};

export default HelpLauncher;
